import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import {
  __HttpRequestModel__,
  HttpRequestModelType,
} from 'shared/domain/httpRequest/httpRequestModel';
import { Services } from 'shared/domain/messages/message';
import {
  IDocumentationUploader,
  UploadDocumentation,
} from 'serviceWorker/repository/documentation/uploadDocumentation';
import {
  UploadDocumentationChange,
  UploadDocumentationVersionChange,
} from 'serviceWorker/repository/documentation/uploadDocumentationChange';
import { swSignals } from 'serviceWorker/events/swSignals';
import { isUploadOf } from './abortedUpload';
import type { IHttpRequestRunner } from 'shared/domain/httpRequest/httpRequestRunner';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { LogLevel } from 'shared/types/logger';
import { broadcastFailUploadedDocumentation } from 'serviceWorker/broadcasts/documentations';

export class DocumentationRequestRunner implements IHttpRequestRunner {
  private abortController: AbortController | undefined;
  private request: __HttpRequestModel__ | undefined;

  constructor(
    private documentationUploader: IDocumentationUploader,
    private uploadDocumentationUseCase: UploadDocumentation,
    private uploadDocumentationChangeUseCase: UploadDocumentationChange,
    private uploadDocumentationVersionChangeUseCase: UploadDocumentationVersionChange,
    private getDocumentation: (id: number) => Promise<DocumentationModel>
  ) {
    swSignals.uploadAborted.subscribe((detail) => {
      if (this.request && isUploadOf(this.request, detail)) {
        this.abortController?.abort();
      }
    });
  }
  async execute(request: __HttpRequestModel__): Promise<boolean> {
    const abortController = new AbortController();
    this.abortController = abortController;
    this.request = request;
    switch (request.method) {
      case 'POST': {
        return this.uploadDocumentation(request, abortController);
      }
      case 'PUT': {
        if (
          request.entityType === HttpRequestModelType.documentationVersion
        ) {
          return this.updateDocumentationVersion(request);
        }
        return this.updateDocumentation(request);
      }
    }
    throw new Error('Not implemented.');
  }

  private async uploadDocumentation(
    request: __HttpRequestModel__,
    abortController: AbortController
  ): Promise<boolean> {
    let documentation: DocumentationModel;
    try {
      documentation = await this.getDocumentation(request.data.localId);
    } catch (error) {
      swLog(
        `Skipping documentation upload: missing local entity ${request.data.localId}`,
        LogLevel.INFO,
        error,
        { query: { localId: request.data.localId } }
      );
      broadcastFailUploadedDocumentation(request.data.localId);
      return true;
    }
    const url = `/project/${request.data.projectId}/documentation`;

    return this.uploadDocumentationUseCase(
      abortController,
      this.documentationUploader,
      documentation,
      url
    );
  }

  private async updateDocumentation(
    request: __HttpRequestModel__
  ): Promise<boolean> {
    let documentation: DocumentationModel;
    try {
      documentation = await this.getDocumentation(request.data.localId);
    } catch (error) {
      swLog(
        `Skipping documentation update: missing local entity ${request.data.localId}`,
        LogLevel.INFO,
        error,
        { query: { localId: request.data.localId } }
      );
      return true;
    }
    if (!documentation._id) {
      return false;
    }
    const url = `/project/${request.data.projectId}/documentation/${documentation._id}`;

    return this.uploadDocumentationChangeUseCase(
      this.documentationUploader,
      url,
      request.data.changes
    );
  }

  private async updateDocumentationVersion(
    request: __HttpRequestModel__
  ): Promise<boolean> {
    const projectId = request.data.projectId;
    const versionId = request.data.versionId;
    const changes = request.data.changes;

    let documentationId: string;
    if (request.data.documentationId) {
      documentationId = request.data.documentationId;
    } else {
      let documentation: DocumentationModel;
      try {
        documentation = await this.getDocumentation(request.data.localId);
      } catch (error) {
        swLog(
          `Skipping documentation version update: missing local entity ${request.data.localId}`,
          LogLevel.INFO,
          error,
          { query: { localId: request.data.localId } }
        );
        return true;
      }
      if (!documentation) {
        swLog(
          `Documentation ${request.data.localId} not found in db`,
          LogLevel.ERROR,
          new Error(`Documentation ${request.data.localId} not found in db`),
          { query: { localId: request.data.localId } }
        );
        return false;
      }
      const version = documentation.versions.find(
        (v) => v._id === versionId
      );
      if (!version) {
        swLog(
          `Version ${versionId} not found in documentation ${request.data.localId}`,
          LogLevel.ERROR,
          new Error(
            `Version ${versionId} not found in documentation ${request.data.localId}`
          ),
          {
            query: {
              localId: request.data.localId,
              versionId: request.data.versionId,
            },
          }
        );
        return false;
      }
      documentationId = documentation._id;
    }

    const url = `/project/${projectId}/documentation/${documentationId}/version/${versionId}`;

    return this.uploadDocumentationVersionChangeUseCase(
      this.documentationUploader,
      url,
      changes
    );
  }
}
